// ============================================================
// Permission Definitions
// ============================================================

export const PermissionDefinitions = {

  // ==================== Roles ====================
  Roles: {
    Default: 'Roles',
    View: 'Roles.View',
    Create: 'Roles.Create',
    Update: 'Roles.Update',
    Delete: 'Roles.Delete',
    AssignPermission: 'Roles.AssignPermission',
    ViewPermission: 'Roles.ViewPermission',
  },

  // ==================== Users ====================
  Users: {
    Default: 'Users',
    View: 'Users.View',
    Create: 'Users.Create',
    Update: 'Users.Update',
    Delete: 'Users.Delete',
    Ban: 'Users.Ban',
    ChangePassword: 'Users.ChangePassword',
    AssignRole: 'Users.AssignRole',
    ChangeBalance: 'Users.ChangeBalance',
  },

  // ==================== Products ====================
  Products: {
    Default: 'Products',
    View: 'Products.View',
    Create: 'Products.Create',
    Update: 'Products.Update',
    Delete: 'Products.Delete',
  },

  // ==================== Categories ====================
  Categories: {
    Default: 'Categories',
    View: 'Categories.View',
    Create: 'Categories.Create',
    Update: 'Categories.Update',
    Delete: 'Categories.Delete',
  },

  // ==================== Brands ====================
  Brands: {
    Default: 'Brands',
    View: 'Brands.View',
    Create: 'Brands.Create',
    Update: 'Brands.Update',
    Delete: 'Brands.Delete',
  },

  // ==================== Orders ====================
  Orders: {
    Default: 'Orders',
    View: 'Orders.View',
    Create: 'Orders.Create',
    Update: 'Orders.Update',
    Delete: 'Orders.Delete',
    ChangeStatus: 'Orders.ChangeStatus',
  },

  // ==================== Stores ====================
  Stores: {
    Default: 'Stores',
    View: 'Stores.View',
    Create: 'Stores.Create',
    Update: 'Stores.Update',
    Delete: 'Stores.Delete',
  },

  // ==================== Banners ====================
  Banners: {
    Default: 'Banners',
    View: 'Banners.View',
    Create: 'Banners.Create',
    Update: 'Banners.Update',
    Delete: 'Banners.Delete',
  },

  // ==================== Wallet ====================
  Wallet: {
    Default: 'Wallet',
    View: 'Wallet.View',
    AddBalance: 'Wallet.AddBalance',
    DeductBalance: 'Wallet.DeductBalance',
    ViewTransactions: 'Wallet.ViewTransactions',
  },

  // ==================== Settings ====================
  Settings: {
    Default: 'Settings',
    View: 'Settings.View',
    Update: 'Settings.Update',
  },

  // ==================== Dashboard ====================
  Dashboard: {
    Default: 'Dashboard',
    ViewStats: 'Dashboard.ViewStats',
    ViewReports: 'Dashboard.ViewReports',
  },

} as const;


// ============================================================
// Helpers
// ============================================================

/**
 * بيرجع كل الصلاحيات الموجودة في الكلاس ده تلقائيًا
 */
export function getAllPermissions(): string[] {
  const permissions: string[] = [];

  Object.values(PermissionDefinitions).forEach(group => {
    Object.values(group).forEach(value => {
      // تجاهل "Default" لأنه مجرد prefix
      const perm = value as string;
      if (perm && perm.includes('.')) {
        permissions.push(perm);
      }
    });
  });

  return permissions;
}

/**
 * بيرجع الصلاحيات مقسّمة بالموديول
 */
export function getGroupedPermissions(): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  Object.entries(PermissionDefinitions).forEach(([moduleName, group]) => {
    const perms = Object.entries(group)
      .filter(([key]) => key !== 'Default')
      .map(([, value]) => value as string);

    if (perms.length > 0) {
      result[moduleName] = perms;
    }
  });

  return result;
}

/**
 * بيرجع كل أسماء الـ Modules
 */
export function getAllModules(): string[] {
  return Object.keys(PermissionDefinitions);
}


// ============================================================
// Types (اختياري - للـ Type Safety)
// ============================================================

export type PermissionModule = keyof typeof PermissionDefinitions;

export type RolePermission =
  typeof PermissionDefinitions.Roles[keyof typeof PermissionDefinitions.Roles];

export type UserPermission =
  typeof PermissionDefinitions.Users[keyof typeof PermissionDefinitions.Users];

export type ProductPermission =
  typeof PermissionDefinitions.Products[keyof typeof PermissionDefinitions.Products];

export type CategoryPermission =
  typeof PermissionDefinitions.Categories[keyof typeof PermissionDefinitions.Categories];

export type BrandPermission =
  typeof PermissionDefinitions.Brands[keyof typeof PermissionDefinitions.Brands];

export type OrderPermission =
  typeof PermissionDefinitions.Orders[keyof typeof PermissionDefinitions.Orders];

export type StorePermission =
  typeof PermissionDefinitions.Stores[keyof typeof PermissionDefinitions.Stores];

export type BannerPermission =
  typeof PermissionDefinitions.Banners[keyof typeof PermissionDefinitions.Banners];

export type WalletPermission =
  typeof PermissionDefinitions.Wallet[keyof typeof PermissionDefinitions.Wallet];

export type SettingPermission =
  typeof PermissionDefinitions.Settings[keyof typeof PermissionDefinitions.Settings];

export type DashboardPermission =
  typeof PermissionDefinitions.Dashboard[keyof typeof PermissionDefinitions.Dashboard];


// ============================================================
// Alternative: Enum-like Constants (لو مش متعود على الـ Object)
// ============================================================

export class Permissions {
  // Roles
  static readonly ROLES_VIEW = 'Roles.View';
  static readonly ROLES_CREATE = 'Roles.Create';
  static readonly ROLES_UPDATE = 'Roles.Update';
  static readonly ROLES_DELETE = 'Roles.Delete';
  static readonly ROLES_ASSIGN_PERMISSION = 'Roles.AssignPermission';
  static readonly ROLES_VIEW_PERMISSION = 'Roles.ViewPermission';

  // Users
  static readonly USERS_VIEW = 'Users.View';
  static readonly USERS_CREATE = 'Users.Create';
  static readonly USERS_UPDATE = 'Users.Update';
  static readonly USERS_DELETE = 'Users.Delete';
  static readonly USERS_BAN = 'Users.Ban';
  static readonly USERS_CHANGE_PASSWORD = 'Users.ChangePassword';
  static readonly USERS_ASSIGN_ROLE = 'Users.AssignRole';
  static readonly USERS_CHANGE_BALANCE = 'Users.ChangeBalance';

  // Products
  static readonly PRODUCTS_VIEW = 'Products.View';
  static readonly PRODUCTS_CREATE = 'Products.Create';
  static readonly PRODUCTS_UPDATE = 'Products.Update';
  static readonly PRODUCTS_DELETE = 'Products.Delete';

  // Categories
  static readonly CATEGORIES_VIEW = 'Categories.View';
  static readonly CATEGORIES_CREATE = 'Categories.Create';
  static readonly CATEGORIES_UPDATE = 'Categories.Update';
  static readonly CATEGORIES_DELETE = 'Categories.Delete';

  // Brands
  static readonly BRANDS_VIEW = 'Brands.View';
  static readonly BRANDS_CREATE = 'Brands.Create';
  static readonly BRANDS_UPDATE = 'Brands.Update';
  static readonly BRANDS_DELETE = 'Brands.Delete';

  // Orders
  static readonly ORDERS_VIEW = 'Orders.View';
  static readonly ORDERS_CREATE = 'Orders.Create';
  static readonly ORDERS_UPDATE = 'Orders.Update';
  static readonly ORDERS_DELETE = 'Orders.Delete';
  static readonly ORDERS_CHANGE_STATUS = 'Orders.ChangeStatus';

  // Stores
  static readonly STORES_VIEW = 'Stores.View';
  static readonly STORES_CREATE = 'Stores.Create';
  static readonly STORES_UPDATE = 'Stores.Update';
  static readonly STORES_DELETE = 'Stores.Delete';

  // Banners
  static readonly BANNERS_VIEW = 'Banners.View';
  static readonly BANNERS_CREATE = 'Banners.Create';
  static readonly BANNERS_UPDATE = 'Banners.Update';
  static readonly BANNERS_DELETE = 'Banners.Delete';

  // Wallet
  static readonly WALLET_VIEW = 'Wallet.View';
  static readonly WALLET_ADD_BALANCE = 'Wallet.AddBalance';
  static readonly WALLET_DEDUCT_BALANCE = 'Wallet.DeductBalance';
  static readonly WALLET_VIEW_TRANSACTIONS = 'Wallet.ViewTransactions';

  // Settings
  static readonly SETTINGS_VIEW = 'Settings.View';
  static readonly SETTINGS_UPDATE = 'Settings.Update';

  // Dashboard
  static readonly DASHBOARD_VIEW_STATS = 'Dashboard.ViewStats';
  static readonly DASHBOARD_VIEW_REPORTS = 'Dashboard.ViewReports';
}


// ============================================================
// Roles Constants
// ============================================================

export const RoleNames = {
  Admin: 'Admin',
  User: 'User',
  Customer: 'Customer',
  Supplier: 'Supplier',
} as const;

export type RoleName = typeof RoleNames[keyof typeof RoleNames];